/* global conditionalblocks */
import Select, { createFilter } from 'react-select';
import TimePicker from 'react-time-picker';
import { optionsManager } from '../../utils/manager.js';

import EDDProductCategorySelect from '../fields/EDDProductCategorySelect/index.js';
import EDDProductSelect from '../fields/EDDProductSelect/index.js';
import PostSelect from '../fields/PostSelect/index.js';
import PostTypeSelect from '../fields/PostTypeSelect/index.js';
import TaxonomySelect from '../fields/TaxonomySelect/index.js';
import TermSelect from '../fields/TermSelect/index.js';
import WCProductCategorySelect from '../fields/WCProductCategorySelect/index.js';
import WCProductSelect from '../fields/WCProductSelect/index.js';
import ButtonWithConfirm from '../misc/ButtonWithConfirm/index.js';

import {
  blankCondition,
  formatArrayForSelect,
  getFormatForMultiSelect,
  shouldDisplaySeparator,
} from '../../utils/helpers.js';
import { findCategoryFromConditionType, getConditionTypes, getSingleConditionType } from '../../utils/registered.js';
import CategoryIcon from '../misc/CategoryIcon.js';

const { __ } = wp.i18n;
const { Fragment } = wp.element;
const { useInstanceId } = wp.compose;
const {
  Icon,
  TextControl,
  DateTimePicker,
  SelectControl,
  Button,
  CheckboxControl,
  TextareaControl,
  RadioControl,
  ExternalLink,
  BaseControl,
  Notice,
} = wp.components;
const { date } = wp.date;

export default function ConditionBuilder({
  className,
  conditions = [],
  onChange = null,
  isPreset = false,
  isSet = false,
}) {
  const manager = optionsManager();
  const instanceId = useInstanceId(ConditionBuilder);

  // Deep clone the conditions, otherwise duplicate blocks will sync.
  // Keep in mind.JSON is fast but not all data types are support.
  // eslint-disable-next-line no-param-reassign
  conditions = JSON.parse(JSON.stringify(conditions));

  // Omit the change.
  const handleChange = (modifiedConditions) => {
    if (typeof onChange === 'function') {
      onChange(modifiedConditions);
    }
  };

  // Prepare options with category labels and values for searching
  const originalOptions = getConditionTypes({ isPreset: isPreset });
  const optionsWithCategoryData = originalOptions.map((group) => ({
    ...group,
    options: group.options.map((option) => ({
      ...option,
      categoryLabel: group.label, // Add category label
      categoryValue: group.value, // Add category value
    })),
  }));

  // Custom filter function for substring matching
  const customFilterOption = (option, rawInput) => {
    const inputValue = rawInput.toLowerCase().trim();
    if (!inputValue) {
      return true; // Show all options if input is empty
    }

    // Combine all searchable fields from the option's data
    // Note: option.data contains the { label, value, categoryLabel, categoryValue } object
    const searchableString = `
      ${option.data.label} 
      ${option.data.value} 
      ${option.data.categoryLabel} 
      ${option.data.categoryValue}`.toLowerCase();

    return searchableString.includes(inputValue);
  };

  const presetValues = () => {
    const presetOptions = [];

    const getPresets = manager.get('presets');

    const allPresets = Array.isArray(getPresets) ? getPresets : [];

    for (const preset of allPresets) {
      presetOptions.push({ value: preset.id, label: preset.name });
    }

    return presetOptions;
  };

  const groupLabelFormat = (data) => {
    return (
      <div className="conditional-blocks-category-wrapper">
        <CategoryIcon category={data} />
        <span className="conditional-blocks-category-label">{data.label}</span>
        <span className="conditional-blocks-badge">{data.options.length}</span>
      </div>
    );
  };

  const SelectedValueFormat = (props) => {
    return (
      <Fragment>
        {props?.data?.value && <CategoryIcon category={findCategoryFromConditionType(props.data.value)} />}
        <span className="conditional-blocks-selected-condition-label">{props.data.label}</span>
      </Fragment>
    );
  };

  const archiveTypes = [
    { value: 'all', label: 'All Archives' },
    { value: 'postTypes', label: 'Post Types (categories, etc)' },
    { value: 'author', label: 'Author', isDisabled: true },
  ];

  const conditionKeys = Object.keys(conditions);

  const lastCondition = conditions.length ? [...conditions].pop()?.type : false;

  return (
    <div className={className}>
      <div className="conditional-blocks-builder-modal__condition_builder_wrapper">
        {conditionKeys.map((value, index) => {
          const condition = conditions[index];

          const registeredCondition = getSingleConditionType(condition?.type);

          return (
            <Fragment key={condition.id + instanceId}>
              <div className="conditional-blocks-builder-modal__single_condition-wrapper">
                <div className="conditional-blocks-builder-modal__single_condition">
                  <BaseControl label={__('Condition', 'conditional-blocks')} __nextHasNoMarginBottom={true}>
                    <Select
                      classNamePrefix="conditional-blocks-select"
                      className="conditional-blocks-select-container"
                      value={
                        registeredCondition?.value
                          ? { value: registeredCondition.value, label: registeredCondition.label }
                          : false
                      } // Requires object with value & label.
                      isSearchable
                      options={optionsWithCategoryData}
                      filterOption={customFilterOption}
                      components={{ SingleValue: SelectedValueFormat }}
                      isClearable={true}
                      placeholder={__('Choose a condition', 'conditional-blocks')}
                      formatGroupLabel={groupLabelFormat}
                      backspaceRemovesValue={true}
                      onChange={(typeChanged) => {
                        const newType = typeChanged ? typeChanged.value : null;

                        const modified = Array.from(conditions);
                        // Reset the whole condition to ensure theres no left over data.
                        modified[index] = blankCondition(modified[index]?.id ? modified[index].id : false);

                        if (newType) {
                          modified[index].type = newType;

                          // Set necessary defaults for some types.
                          if (newType === 'dateRange') {
                            // Make sure start and end times are set for date ranges.
                            // eslint-disable-next-line prettier/prettier
                            modified[index].startTime = date('Y-m-d\\TH:i:s');
                            modified[index].endTime = date('Y-m-d\\TH:i:s');
                            modified[index].hasEndDate = false;
                          }
                        }

                        handleChange(modified);
                      }}
                    />
                  </BaseControl>
                  {registeredCondition.configuration && (
                    <>
                      {registeredCondition.configuration?.description && (
                        <BaseControl
                          key={`${condition.id}-description`}
                          help={registeredCondition.configuration.description}
                          __nextHasNoMarginBottom={true}
                        />
                      )}

                      {registeredCondition.configuration.fields?.map((field, fieldIndex) => {
                        /**
                         * Requires: Don't render field if other condition fields don't have required values.
                         */
                        if (field?.requires) {
                          let hasRequirements = true;

                          // biome-ignore lint/complexity/noForEach: Change & test in future versions.
                          Object.entries(field.requires).forEach((requirement) => {
                            const [fieldKey, expectedValue] = requirement;

                            // Exit early if field isn't set.
                            if (!condition[fieldKey]) {
                              hasRequirements = false;
                              return false;
                            }

                            const originalField = registeredCondition.configuration.fields.find(
                              (e) => e?.key === fieldKey,
                            );

                            const isRequirementArray = Array.isArray(expectedValue);

                            let currentValue;

                            // Determine the field type
                            // Select fields have {value, label} - can be single or multiple.
                            if (originalField.type === 'select') {
                              if (originalField?.attributes?.multiple === true) {
                                currentValue = condition[fieldKey].map((selected) => selected.value);

                                const similarValues = currentValue.filter((element) =>
                                  isRequirementArray
                                    ? expectedValue.includes(element)
                                    : [expectedValue].includes(element),
                                );

                                if (!similarValues.length) {
                                  hasRequirements = false;
                                  return false; // Exit foreach.
                                }
                              } else {
                                currentValue = condition[fieldKey]?.value;

                                const hasRequiredSelect = isRequirementArray
                                  ? expectedValue.includes(currentValue)
                                  : JSON.stringify(currentValue) === JSON.stringify(expectedValue);

                                if (!hasRequiredSelect) {
                                  hasRequirements = false;
                                  return false; // Exit foreach.
                                }
                              }
                            } else {
                              currentValue = condition[fieldKey];

                              const hasRequiredValue = isRequirementArray
                                ? expectedValue.includes(currentValue)
                                : JSON.stringify(currentValue) === JSON.stringify(expectedValue);

                              if (!hasRequiredValue) {
                                hasRequirements = false;
                                return false; // Exit foreach.
                              }
                            }
                          });

                          if (!hasRequirements) {
                            return ''; // Exit condition field render.
                          }
                        }

                        if (field.type === 'text') {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                            <div key={fieldIndex + field.key} className="conditional-blocks-builder-modal__row">
                              <TextControl
                                label={field?.attributes?.label ?? ''}
                                placeholder={field?.attributes?.placeholder ?? ''}
                                help={field?.attributes?.help ?? ''}
                                value={
                                  condition[field.key] !== undefined
                                    ? condition[field.key]
                                    : field?.attributes?.value ?? ''
                                }
                                __nextHasNoMarginBottom={true}
                                __next40pxDefaultSize={true}
                                onChange={(nextValue) => {
                                  const modified = Array.from(conditions);
                                  // Update the condition.

                                  modified[index][field.key] = nextValue;

                                  handleChange(modified);
                                }}
                              />
                            </div>
                          );
                        }

                        if (field.type === 'select') {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                            <div key={fieldIndex + field.key} className="conditional-blocks-builder-modal__row">
                              <BaseControl
                                id={fieldIndex + field.key}
                                label={field?.attributes?.label ?? ''}
                                help={field?.attributes?.help ?? ''}
                                __nextHasNoMarginBottom={true}
                              >
                                <Select
                                  classNamePrefix="conditional-blocks-select"
                                  className="conditional-blocks-select-container"
                                  value={
                                    condition[field.key] !== undefined
                                      ? condition[field.key]
                                      : field?.attributes?.value ?? ''
                                  }
                                  isSearchable={field?.attributes?.searchable ?? false}
                                  isMulti={field?.attributes?.multiple}
                                  placeholder={field?.attributes?.placeholder ?? 'select...'}
                                  options={field?.options ?? []}
                                  onChange={(nextValue) => {
                                    const modified = Array.from(conditions);

                                    modified[index][field.key] = nextValue;

                                    handleChange(modified);
                                  }}
                                />
                              </BaseControl>
                            </div>
                          );
                        }

                        if (field.type === 'radio') {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                            <div key={fieldIndex + field.key} className="conditional-blocks-builder-modal__row">
                              <RadioControl
                                label={field?.attributes?.label ?? ''}
                                help={field?.attributes?.help ?? ''}
                                placeholder={field?.attributes?.placeholder ?? ''}
                                selected={condition[field.key] ? condition[field.key] : field?.attributes?.value ?? ''}
                                options={field?.options ?? null}
                                onChange={(nextValue) => {
                                  const modified = Array.from(conditions);
                                  // Update the condition.

                                  modified[index][field.key] = nextValue;

                                  handleChange(modified);
                                }}
                              />
                            </div>
                          );
                        }

                        if (field.type === 'number') {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                            <div key={fieldIndex + field.key} className="conditional-blocks-builder-modal__row">
                              <TextControl
                                label={field?.attributes?.label ?? ''}
                                placeholder={field?.attributes?.placeholder ?? ''}
                                step={field?.attributes?.step ?? ''}
                                min={field?.attributes?.min ?? ''}
                                max={field?.attributes?.max ?? ''}
                                help={field?.attributes?.help ?? ''}
                                value={
                                  condition[field.key] !== undefined
                                    ? condition[field.key]
                                    : field?.attributes?.value ?? ''
                                }
                                type="number"
                                __nextHasNoMarginBottom={true}
                                __next40pxDefaultSize={true}
                                onChange={(nextValue) => {
                                  const modified = Array.from(conditions);
                                  // Update the condition.

                                  modified[index][field.key] = nextValue;

                                  handleChange(modified);
                                }}
                              />
                            </div>
                          );
                        }

                        if (field.type === 'EDDProductSelect') {
                          return (
                            <BaseControl
                              key={`${condition.id}-${field.key}`}
                              id={fieldIndex + field.key}
                              label={field?.attributes?.label ?? ''}
                              help={field?.attributes?.help ?? ''}
                              __nextHasNoMarginBottom={true}
                            >
                              <EDDProductSelect
                                placeholder={field?.attributes?.placeholder ?? ''}
                                value={
                                  condition[field.key] !== undefined
                                    ? condition[field.key]
                                    : field?.attributes?.value ?? ''
                                }
                                onChange={(nextValue) => {
                                  const modified = Array.from(conditions);
                                  // Update the condition.

                                  modified[index][field.key] = nextValue;

                                  handleChange(modified);
                                }}
                              />
                            </BaseControl>
                          );
                        }

                        if (field.type === 'EDDProductCategorySelect') {
                          return (
                            <Fragment key={`${condition.id}-${field.key}`}>
                              <BaseControl
                                id={fieldIndex + field.key}
                                label={field?.attributes?.label ?? ''}
                                help={field?.attributes?.help ?? ''}
                                __nextHasNoMarginBottom={true}
                              >
                                <EDDProductCategorySelect
                                  placeholder={field?.attributes?.placeholder ?? ''}
                                  value={
                                    condition[field.key] !== undefined
                                      ? condition[field.key]
                                      : field?.attributes?.value ?? ''
                                  }
                                  onChange={(nextValue) => {
                                    const modified = Array.from(conditions);
                                    // Update the condition.

                                    modified[index][field.key] = nextValue;

                                    handleChange(modified);
                                  }}
                                />
                              </BaseControl>
                            </Fragment>
                          );
                        }

                        if (field.type === 'blockAction') {
                          return (
                            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                            <div key={fieldIndex + field.key} className="conditional-blocks-builder-modal__row">
                              <BlockAction
                                condition={condition}
                                index={index}
                                handleChange={handleChange}
                                conditions={conditions}
                              />
                            </div>
                          );
                        }

                        return '';
                      })}
                    </>
                  )}
                  {condition.type === 'userLoggedIn' && (
                    <p>
                      {__(
                        'The selected block will only be visible to users who are logged into the site.',
                        'conditional-blocks',
                      )}
                    </p>
                  )}
                  {condition.type === 'userLoggedOut' && (
                    <p>
                      {__(
                        "The selected block will only be visible to users who aren't logged into the site.",
                        'conditional-blocks',
                      )}
                    </p>
                  )}
                  {condition.type === 'presets' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <div className="conditional-blocks-builder-modal__row">
                        <p>
                          {__(
                            'Presets are conditions that can be shared by multiple blocks. You can edit presets from the Preset Manager.',
                            'conditional-blocks',
                          )}
                        </p>
                      </div>
                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={getFormatForMultiSelect(condition?.presets)} // Requires object with value & label.
                        isSearchable
                        isMulti
                        options={presetValues()}
                        onChange={(newPresets) => {
                          const modified = Array.from(conditions);

                          modified[index].presets = newPresets;

                          handleChange(modified);
                        }}
                      />
                      <h4>{__('Presets Action', 'conditional-blocks')}</h4>

                      <RadioControl
                        label={__('Requirement for the block to be visible.', 'conditional-blocks')}
                        selected={condition?.requirement ? condition.requirement : 'all'}
                        options={[
                          {
                            label: __('All presets must be successful - (AND Logic)', 'conditional-blocks'),
                            value: 'all',
                          },
                          {
                            label: __('At least one preset must be successful - (OR logic)', 'conditional-blocks'),
                            value: 'any',
                          },
                        ]}
                        onChange={(nextValue) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          modified[index].requirement = nextValue;

                          handleChange(modified);
                        }}
                      />
                    </div>
                  )}
                  {condition.type === 'userRoles' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <h4>{__('User Roles', 'conditional-blocks')}</h4>

                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Select one or multiple user roles. The current user must have any of selected roles to trigger the block action.',
                          'conditional-blocks',
                        )}
                      </div>

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={getFormatForMultiSelect(condition?.allowedRoles)} // Requires object with value & label.
                        isSearchable
                        isMulti
                        options={manager.get('roles') || []}
                        onChange={(newRoles) => {
                          const modified = Array.from(conditions);

                          modified[index].allowedRoles = newRoles;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </div>
                  )}
                  {condition.type === 'userMeta' && (
                    <Fragment>
                      <p>
                        {__('Display the selected block based on the User Metadata (Fields)', 'conditional-blocks')}
                      </p>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextControl
                          label={__('User Meta Key', 'conditional-blocks')}
                          placeholder="first_name"
                          help={__('Use meta key.', 'conditional-blocks')}
                          value={condition?.metaKey ? condition.metaKey : ''}
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].metaKey = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>

                      <div className="conditional-blocks-builder-modal__row">
                        <SelectControl
                          className="conditional-blocks-builder-modal__role_select"
                          label={__('Select operator', 'conditional-blocks')}
                          value={condition?.metaOperator ? condition.metaOperator : ''}
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                          onChange={(newValue) => {
                            const modified = Array.from(conditions);

                            modified[index].metaOperator = newValue;

                            handleChange(modified);
                          }}
                          options={[
                            { label: 'Choose Operator', value: '' },
                            { label: 'is true', value: 'true' },
                            { label: 'is false', value: 'false' },
                            { label: 'is empty', value: 'empty' },
                            { label: 'is not empty', value: 'notEmpty' },
                            { label: 'equal', value: '===' },
                            { label: "doesn't equal", value: '!==' },
                            { label: 'contains', value: 'contains' },
                            { label: "doesn't contain", value: 'doesNotContain' },
                          ]}
                        />
                      </div>
                      {(condition.metaOperator === '===' ||
                        condition.metaOperator === '!==' ||
                        condition.metaOperator === 'contains' ||
                        condition.metaOperator === 'doesNotContain') && (
                        <div className="conditional-blocks-builder-modal__row">
                          <TextControl
                            label={__('Post Meta Value', 'conditional-blocks')}
                            placeholder="Morgan"
                            help={__('The expected value of your post meta data.', 'conditional-blocks')}
                            value={condition?.metaValue ? condition.metaValue : ''}
                            __nextHasNoMarginBottom={true}
                            __next40pxDefaultSize={true}
                            onChange={(nextValue) => {
                              const modified = Array.from(conditions);
                              // Update the condition.

                              modified[index].metaValue = nextValue;

                              handleChange(modified);
                            }}
                          />
                        </div>
                      )}
                    </Fragment>
                  )}
                  {condition.type === 'postType' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action if one of the selected Post Types is being viewed.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Select Post Type', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <PostTypeSelect
                          isMulti
                          onChange={(postTypes) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].postTypes = postTypes;

                            handleChange(modified);
                          }}
                          value={condition?.postTypes || false}
                          placeholder={__('Search Post Types..', 'conditional-blocks')}
                        />
                      </div>
                      <h4>{__('Block Action', 'conditional-blocks')}</h4>

                      <RadioControl
                        label={__('If this condition is true, then:', 'conditional-blocks')}
                        selected={condition?.blockAction ? condition.blockAction : 'showBlock'}
                        options={[
                          { label: __('Block should be visible', 'conditional-blocks'), value: 'showBlock' },
                          { label: __('Block should be hidden', 'conditional-blocks'), value: 'hideBlock' },
                        ]}
                        onChange={(nextValue) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          modified[index].blockAction = nextValue;

                          handleChange(modified);
                        }}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'posts' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action if the current post matches the selected posts.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Select Post Type', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <PostTypeSelect
                          onChange={(postType) => {
                            const modified = Array.from(conditions);
                            // Update the condition.
                            modified[index].postType = postType;
                            modified[index].posts = false;

                            handleChange(modified);
                          }}
                          value={condition?.postType || false}
                          placeholder={__('Search Post Types..', 'conditional-blocks')}
                        />
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <PostSelect
                          isMulti
                          postType={condition?.postType?.value}
                          isDisabled={!condition?.postType?.value}
                          onChange={(posts) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].posts = posts;

                            handleChange(modified);
                          }}
                          value={condition?.posts || false}
                          placeholder={__('Search Posts..', 'conditional-blocks')}
                        />
                      </div>
                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}

                  {condition.type === 'postTaxonomyTerms' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action if the current post has the selected taxonomy and (optionally) terms.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Select Post Type', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <PostTypeSelect
                          onChange={(postType) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].postType = postType;
                            modified[index].taxonomy = false;
                            modified[index].terms = false;

                            handleChange(modified);
                          }}
                          value={condition?.postType || false}
                          placeholder={__('Search Post Types..', 'conditional-blocks')}
                        />
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <TaxonomySelect
                          postType={condition?.postType?.value}
                          isDisabled={!condition?.postType?.value}
                          onChange={(taxonomy) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].taxonomy = taxonomy;
                            modified[index].terms = false;

                            handleChange(modified);
                          }}
                          value={condition?.taxonomy || false}
                          placeholder={__('Search Taxonomies..', 'conditional-blocks')}
                        />
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <TermSelect
                          isMulti
                          postType={condition?.postType?.value}
                          taxonomy={condition?.taxonomy?.value}
                          isDisabled={!condition?.taxonomy?.value}
                          onChange={(terms) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].terms = terms;

                            handleChange(modified);
                          }}
                          value={condition?.terms || false}
                          placeholder={__('Search Terms.. (optional)', 'conditional-blocks')}
                        />
                      </div>
                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'archive' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action when the current page is an archive. You can optionally select a specific archive.',
                          'conditional-blocks',
                        )}
                      </div>

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        defaultValue={{ value: 'all', label: 'All Archives' }}
                        // value={condition?.archiveType} // Requires object with value & label.
                        value={archiveTypes.find((obj) => obj.value === condition?.archiveType)}
                        options={archiveTypes}
                        onChange={(obj) => {
                          const modified = Array.from(conditions);

                          modified[index].archiveType = obj.value;

                          handleChange(modified);
                        }}
                      />

                      {condition?.archiveType === 'postTypes' && (
                        <Fragment>
                          <h4>{__('Select Post Type', 'conditional-blocks')}</h4>
                          <div className="conditional-blocks-builder-modal__row">
                            <PostTypeSelect
                              onChange={(postType) => {
                                const modified = Array.from(conditions);
                                // Update the condition.

                                modified[index].postType = postType;
                                modified[index].taxonomy = false;
                                modified[index].terms = false;

                                handleChange(modified);
                              }}
                              value={condition?.postType || false}
                              placeholder={__('Search Post Types..', 'conditional-blocks')}
                            />
                          </div>
                          <div className="conditional-blocks-builder-modal__row">
                            <TaxonomySelect
                              postType={condition?.postType?.value}
                              isDisabled={!condition?.postType?.value}
                              onChange={(taxonomy) => {
                                const modified = Array.from(conditions);
                                // Update the condition.

                                modified[index].taxonomy = taxonomy;
                                modified[index].terms = false;

                                handleChange(modified);
                              }}
                              value={condition?.taxonomy || false}
                              placeholder={__('Search Taxonomies..', 'conditional-blocks')}
                            />
                          </div>
                          <div className="conditional-blocks-builder-modal__row">
                            <TermSelect
                              isMulti
                              postType={condition?.postType?.value}
                              taxonomy={condition?.taxonomy?.value}
                              isDisabled={!condition?.taxonomy?.value}
                              onChange={(terms) => {
                                const modified = Array.from(conditions);
                                // Update the condition.

                                modified[index].terms = terms;

                                handleChange(modified);
                              }}
                              value={condition?.terms || false}
                              placeholder={__('Search Terms..', 'conditional-blocks')}
                            />
                          </div>

                          {condition?.terms?.length >= 1 && (
                            <Fragment>
                              {' '}
                              <h4>{__('Child Archive Pages', 'conditional-blocks')}</h4>
                              <CheckboxControl
                                __nextHasNoMarginBottom
                                label={__('Automatically include child terms of selected terms.', 'conditional-blocks')}
                                checked={!!condition?.includeChildTerms}
                                onChange={(checked) => {
                                  const modified = Array.from(conditions);
                                  // Update the condition.

                                  modified[index].includeChildTerms = checked;

                                  handleChange(modified);
                                }}
                              />
                            </Fragment>
                          )}
                        </Fragment>
                      )}

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'userAgents' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <p>
                        {__(
                          'Trigger the block action when current visitor matches any of the selected devices and browsers.',
                          'conditional-blocks',
                        )}
                      </p>

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={getFormatForMultiSelect(condition?.allowedAgents)} // Requires object with value & label.
                        isSearchable
                        isMulti
                        options={[
                          { label: 'iPhone', value: 'iphone' },
                          { label: 'Android Device', value: 'android' },
                          { label: 'MacOS (includes iPad)', value: 'macintosh' },
                          { label: 'Windows', value: 'windows' },
                          { label: 'Linux', value: 'linux' },
                          { label: 'Google Chrome', value: 'chrome' },
                          { label: 'FireFox', value: 'firefox' },
                          { label: 'Microsoft Edge', value: 'edge' },
                          { label: 'Safari', value: 'safari' },
                        ]}
                        onChange={(newAgents) => {
                          const modified = Array.from(conditions);

                          modified[index].allowedAgents = newAgents;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </div>
                  )}
                  {condition.type === 'cookies' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>{__('Trigger the block action if any of the cookies present in the users browser.')}</p>
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextareaControl
                          label={__('Selected Cookies', 'conditional-blocks')}
                          placeholder="user_allowed_promo"
                          value={condition?.selectedCookies ? condition.selectedCookies : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].selectedCookies = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>
                      <RadioControl
                        label={__('Block Action', 'conditional-blocks')}
                        selected={
                          condition?.shouldShowForSelectedCookies ? condition.shouldShowForSelectedCookies : 'yes'
                        }
                        options={[
                          { value: 'yes', label: __('Show if cookies are present', 'conditional-blocks') },
                          { value: 'no', label: __('Hide if cookies are not present', 'conditional-blocks') },
                        ]}
                        onChange={(option) => {
                          const modified = Array.from(conditions);

                          modified[index].shouldShowForSelectedCookies = option;

                          handleChange(modified);
                        }}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'postMeta' && (
                    <Fragment>
                      <p>
                        {__(
                          'Display the selected block if the current post meta (Custom Fields) matches the selection.',
                          'conditional-blocks',
                        )}
                      </p>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextControl
                          label={__('Post Meta Key', 'conditional-blocks')}
                          placeholder="_favorite_site"
                          help={__('Use custom meta key.', 'conditional-blocks')}
                          value={condition?.metaKey ? condition.metaKey : ''}
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].metaKey = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>

                      <div className="conditional-blocks-builder-modal__row">
                        <SelectControl
                          className="conditional-blocks-builder-modal__role_select"
                          label={__('Select operator', 'conditional-blocks')}
                          value={condition?.metaOperator ? condition.metaOperator : ''}
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                          onChange={(newValue) => {
                            const modified = Array.from(conditions);

                            modified[index].metaOperator = newValue;

                            handleChange(modified);
                          }}
                          options={[
                            { label: 'Choose Operator', value: '' },
                            { label: 'is true', value: 'true' },
                            { label: 'is false', value: 'false' },
                            { label: 'is empty', value: 'empty' },
                            { label: 'is not empty', value: 'notEmpty' },
                            { label: 'equal', value: '===' },
                            { label: "doesn't equal", value: '!==' },
                            { label: 'contains', value: 'contains' },
                            { label: "doesn't contain", value: 'doesNotContain' },
                          ]}
                        />
                      </div>
                      {(condition.metaOperator === '===' ||
                        condition.metaOperator === '!==' ||
                        condition.metaOperator === 'contains' ||
                        condition.metaOperator === 'doesNotContain') && (
                        <div className="conditional-blocks-builder-modal__row">
                          <TextControl
                            label={__('Post Meta Value', 'conditional-blocks')}
                            placeholder="conditionalblocks.com"
                            help={__('The expected value of your post meta data.', 'conditional-blocks')}
                            value={condition?.metaValue ? condition.metaValue : ''}
                            __nextHasNoMarginBottom={true}
                            __next40pxDefaultSize={true}
                            onChange={(nextValue) => {
                              const modified = Array.from(conditions);
                              // Update the condition.

                              modified[index].metaValue = nextValue;

                              handleChange(modified);
                            }}
                          />
                        </div>
                      )}
                    </Fragment>
                  )}
                  {condition.type === 'postIds' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>
                          {__(
                            'Each post/page in WordPress has a unique ID. You can use the ID to make sure the block on display on that post.',
                            'conditional-blocks',
                          )}
                        </p>
                        <RadioControl
                          label={__('Show the block on post/page:', 'conditional-blocks')}
                          selected={condition?.equal ? condition.equal : 'equal'}
                          options={[
                            { value: 'equal', label: __('IDs equal below IDs', 'conditional-blocks') },
                            { value: 'notEqual', label: __('IDs not equal to below IDs', 'conditional-blocks') },
                          ]}
                          onChange={(option) => {
                            const modified = Array.from(conditions);

                            modified[index].equal = option;

                            handleChange(modified);
                          }}
                        />
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextareaControl
                          label={__('Post IDs (One per line)', 'conditional-blocks')}
                          placeholder="92017&#10;32020"
                          value={condition?.postIds ? condition.postIds : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].postIds = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>
                    </Fragment>
                  )}
                  {condition.type === 'urlPaths' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>{__('Trigger the block action based on the below URL paths.', 'conditional-blocks')}</p>
                      </div>

                      <h4>{__('URL Paths', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextareaControl
                          label={__('URL Paths (One per line)', 'conditional-blocks')}
                          placeholder="/blog/introducing-url-paths/&#10;/features/"
                          value={condition?.selectedPaths ? condition.selectedPaths : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].selectedPaths = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>

                      <h4>{__('Block Action', 'conditional-blocks')}</h4>

                      <RadioControl
                        label={__('What is the requirement for the block to visible?', 'conditional-blocks')}
                        selected={condition?.shouldContain ? condition.shouldContain : 'yes'}
                        options={[
                          {
                            value: 'yes',
                            label: __('The current URL path should match any of the above paths', 'conditional-blocks'),
                          },
                          {
                            value: 'no',
                            label: __('The current URL path should not match any above paths', 'conditional-blocks'),
                          },
                        ]}
                        onChange={(option) => {
                          const modified = Array.from(conditions);

                          modified[index].shouldContain = option;

                          handleChange(modified);
                        }}
                      />
                    </Fragment>
                  )}

                  {condition.type === 'phpLogic' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>
                          {__('Read more about this condition in the', 'conditional-blocks')}{' '}
                          <ExternalLink href="https://conditionalblocks.com/docs/advanced/php-logic/?utm_source=conditional-blocks-pro&utm_medium=referral&utm_campaign=modal">
                            {__('PHP Logic documentation.', 'conditional-blocks')}
                          </ExternalLink>
                        </p>
                        <p>Registered custom functions:</p>
                        <div className="conditional-blocks-grid">
                          {conditionalblocks.php_logic_functions.map((functionName) => (
                            <code key={functionName}>{functionName}</code>
                          ))}
                        </div>
                        <TextareaControl
                          label={__('PHP Logic', 'conditional-blocks')}
                          placeholder="has_post_thumbnail()"
                          help={__(
                            'Add valid PHP conditional tags. The functions must return TRUE for the selected block to be visible. This is a developer feature.',
                            'conditional-blocks',
                          )}
                          value={condition?.phpLogic ? condition.phpLogic : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].phpLogic = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>
                    </Fragment>
                  )}

                  {condition.type === 'geolocationContinent' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <h4>{__('Geolocation - Continent', 'conditional-blocks')}</h4>

                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Use the IP address of the current visitor to detect if they are from a specific continent.',
                          'conditional-blocks',
                        )}
                      </div>

                      {!conditionalblocks.ipinfo_api_key && (
                        <div className="conditional-blocks-builder-modal__row">
                          <Notice status="warning" isDismissible={false}>
                            {__(
                              'A Geolocation service API key is required to use this condition. Set your API key in the settings tab.',
                              'conditional-blocks',
                            )}
                          </Notice>
                        </div>
                      )}

                      {!conditionalblocks.has_valid_ip_address && (
                        <div className="conditional-blocks-builder-modal__row">
                          <Notice status="info" isDismissible={false}>
                            {__(
                              'Development environment detected. The condition requires an online WordPress environment to correctly detect geolocation. See our documentation for local testing.',
                              'conditional-blocks',
                            )}
                          </Notice>
                        </div>
                      )}

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={condition?.continent ?? []} // Requires object with value & label.
                        isSearchable
                        options={formatArrayForSelect(conditionalblocks.geolocation_continents, true)}
                        onChange={(data) => {
                          const modified = Array.from(conditions);
                          modified[index].continent = data;
                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </div>
                  )}

                  {condition.type === 'geolocationCountry' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <h4>{__('Geolocation - Country', 'conditional-blocks')}</h4>

                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Use the IP address of the current visitor to detect if they are from a specific country.',
                          'conditional-blocks',
                        )}
                      </div>

                      {!conditionalblocks.ipinfo_api_key && (
                        <div className="conditional-blocks-builder-modal__row">
                          <Notice status="warning" isDismissible={false}>
                            {__(
                              'A Geolocation service API key is required to use this condition. Set your API key in the settings tab.',
                              'conditional-blocks',
                            )}
                          </Notice>
                        </div>
                      )}

                      {!conditionalblocks.has_valid_ip_address && (
                        <div className="conditional-blocks-builder-modal__row">
                          <Notice status="info" isDismissible={false}>
                            {__(
                              'Development environment detected. The condition requires an online WordPress environment to correctly detect geolocation. See our documentation for local testing.',
                              'conditional-blocks',
                            )}
                          </Notice>
                        </div>
                      )}

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={condition?.country ?? []} // Requires object with value & label.
                        isSearchable
                        options={formatArrayForSelect(conditionalblocks.geolocation_countries, true)}
                        onChange={(data) => {
                          const modified = Array.from(conditions);

                          modified[index].country = data;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </div>
                  )}

                  {condition.type === 'responsiveScreenSizes' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <p>
                        {__(
                          "The selected block will automatically hide on screens sizes that aren't selected. You can edit screen widths from the settings tab.",
                          'conditional-blocks',
                        )}
                      </p>
                      <CheckboxControl
                        __nextHasNoMarginBottom
                        key={`${instanceId}_mobile`}
                        label={__('Show on Mobile screens', 'conditional-blocks')}
                        checked={!!condition?.showOn?.includes('showMobileScreen')}
                        onChange={(checked) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          if (!Array.isArray(modified[index].showOn)) {
                            modified[index].showOn = [];
                          }

                          if (checked) {
                            modified[index].showOn.push('showMobileScreen');
                          } else {
                            const findIndex = modified[index].showOn.indexOf('showMobileScreen');
                            if (index > -1) {
                              modified[index].showOn.splice(findIndex, 1);
                            }
                          }

                          handleChange(modified);
                        }}
                      />
                      <CheckboxControl
                        __nextHasNoMarginBottom
                        key={`${instanceId}_tablet`}
                        label={__('Show on Tablet screens', 'conditional-blocks')}
                        checked={!!condition?.showOn?.includes('showTabletScreen')}
                        onChange={(checked) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          if (!Array.isArray(modified[index].showOn)) {
                            modified[index].showOn = [];
                          }

                          if (checked) {
                            modified[index].showOn.push('showTabletScreen');
                          } else {
                            const findIndex = modified[index].showOn.indexOf('showTabletScreen');
                            if (index > -1) {
                              modified[index].showOn.splice(findIndex, 1);
                            }
                          }

                          handleChange(modified);
                        }}
                      />
                      <CheckboxControl
                        __nextHasNoMarginBottom
                        key={`${instanceId}_desktop`}
                        label={__('Show on Desktop screens', 'conditional-blocks')}
                        checked={!!condition?.showOn?.includes('showDesktopScreen')}
                        onChange={(checked) => {
                          const modified = Array.from(conditions);
                          // Update the condition.
                          if (!Array.isArray(modified[index].showOn)) {
                            modified[index].showOn = [];
                          }

                          if (checked) {
                            modified[index].showOn.push('showDesktopScreen');
                          } else {
                            const findIndex = modified[index].showOn.indexOf('showDesktopScreen');
                            if (index > -1) {
                              modified[index].showOn.splice(findIndex, 1);
                            }
                          }

                          handleChange(modified);
                        }}
                      />
                    </div>
                  )}
                  {condition.type === 'dateRange' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>
                          {__(
                            'Date conditions use the timezone from your WordPress time settings. The selected block will only be visible after the start date. You can optionally select an end date.',
                            'conditional-blocks',
                          )}
                        </p>
                      </div>
                      <h4>{__('Start Date', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <DateTimePicker
                          is12Hour
                          currentDate={
                            Number.isInteger(condition.startTime) ? condition.startTime * 1000 : condition.startTime
                          }
                          onChange={(newTime) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].startTime = newTime;

                            handleChange(modified);
                          }}
                        />
                      </div>

                      <div className="conditional-blocks-builder-modal__row">
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Choose end date?', 'conditional-blocks')}
                          checked={!!condition?.hasEndDate}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].hasEndDate = checked;

                            handleChange(modified);
                          }}
                        />
                      </div>
                      {condition?.hasEndDate && (
                        <Fragment>
                          <h4>{__('End Date', 'conditional-blocks')}</h4>
                          <div className="conditional-blocks-builder-modal__row">
                            <DateTimePicker
                              is12Hour
                              currentDate={
                                Number.isInteger(condition.endTime) ? condition.endTime * 1000 : condition.endTime
                              }
                              onChange={(newTime) => {
                                const modified = Array.from(conditions);
                                // Update the condition.

                                modified[index].endTime = newTime;

                                handleChange(modified);
                              }}
                            />
                          </div>
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                  {condition.type === 'dateRecurring' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>
                          {__('Choose the days and time of day that the block will be visible.', 'conditional-blocks')}
                        </p>
                        <p>
                          {__(
                            'Date conditions use the timezone from your WordPress time settings',
                            'conditional-blocks',
                          )}
                        </p>
                      </div>
                      <h4>{__('Recurring Time', 'conditional-blocks')}</h4>

                      <div className="conditional-blocks-builder-modal__row">
                        <p>{__('Start Time (24-Hour)')}</p>
                        <TimePicker
                          className="conditional-blocks-time-picker"
                          format="H:m"
                          disableClock
                          clearIcon={null}
                          maxTime={condition?.endTime || false}
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].startTime = number;

                            handleChange(modified);
                          }}
                          value={condition?.startTime || ''}
                        />
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <p>{__('Stop Time (24-Hour)')}</p>
                        <TimePicker
                          className="conditional-blocks-time-picker"
                          format="H:m"
                          clearIcon={null}
                          disableClock
                          minTime={condition?.startTime || false}
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].endTime = number;

                            handleChange(modified);
                          }}
                          value={condition?.endTime || ''}
                        />
                      </div>

                      <h4>{__('Recurring Days', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Monday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('1')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('1');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('1');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Tuesday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('2')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('2');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('2');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Wednesday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('3')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('3');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('3');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Thursday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('4')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('4');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('4');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Friday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('5')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('5');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('5');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Saturday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('6')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('6');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('6');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                        <CheckboxControl
                          __nextHasNoMarginBottom
                          label={__('Sunday', 'conditional-blocks')}
                          checked={!!condition?.recurringDays?.includes('0')}
                          onChange={(checked) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            if (!Array.isArray(modified[index].recurringDays)) {
                              modified[index].recurringDays = [];
                            }

                            if (checked) {
                              modified[index].recurringDays.push('0');
                            } else {
                              const findIndex = modified[index].recurringDays.indexOf('0');
                              if (index > -1) {
                                modified[index].recurringDays.splice(findIndex, 1);
                              }
                            }

                            handleChange(modified);
                          }}
                        />
                      </div>
                    </Fragment>
                  )}
                  {condition.type === 'domainReferrers' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the block action when visitors who have been directed to your page from an another domain. This condition requires the third-party server to pass the referring information.',
                          'conditional-blocks',
                        )}
                      </div>

                      <h4>{__('Domain referrers (comma separate for multiple)', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextareaControl
                          placeholder="conditionalblocks.com, puri.io, google.com"
                          value={condition?.domainReferrers ? condition.domainReferrers : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].domainReferrers = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>
                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'queryStrings' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action based on the query strings detected in the URL.',
                          'conditional-blocks',
                        )}
                      </div>
                      <div className="conditional-blocks-builder-modal__row">
                        <h4> {__('Check Patterns', 'conditional-blocks')}</h4>
                        <TextareaControl
                          help={__(
                            'For each line, input one pattern. Patterns are matched if partially found. Include longer patterns to be specific.',
                            'conditional-blocks',
                          )}
                          placeholder="testing=query-string&#10;success=true&#10;"
                          value={condition?.allowedStrings ? condition.allowedStrings : ''}
                          __nextHasNoMarginBottom={true}
                          onChange={(nextValue) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].allowedStrings = nextValue;

                            handleChange(modified);
                          }}
                        />
                      </div>

                      <RadioControl
                        label={__('Trigger', 'conditional-blocks')}
                        selected={condition?.requirement ? condition.requirement : 'present'}
                        options={[
                          {
                            label: __('Patterns detected in the URL (one or more)', 'conditional-blocks'),
                            value: 'present',
                          },
                          { label: __('No patterns detected in the URL', 'conditional-blocks'), value: 'notPresent' },
                        ]}
                        onChange={(nextValue) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          modified[index].requirement = nextValue;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}

                  {condition.type === 'wcGeoLocation' && (
                    <div className="conditional-blocks-builder-modal__row">
                      <h4>{__('Geolocation', 'conditional-blocks')}</h4>

                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Detect if the current visitor is from a specific country based on their IP address.',
                          'conditional-blocks',
                        )}
                      </div>

                      <div className="conditional-blocks-builder-modal__row">
                        {`IP: ${conditionalblocks.wc_geo_ip} `}{' '}
                        {conditionalblocks?.wc_geo_ip_location?.country ?? 'N/A'}
                      </div>

                      <Select
                        classNamePrefix="conditional-blocks-select"
                        className="conditional-blocks-select-container"
                        value={getFormatForMultiSelect(condition?.countries)} // Requires object with value & label.
                        isSearchable
                        isMulti
                        options={formatArrayForSelect(conditionalblocks.wc_countries)}
                        onChange={(data) => {
                          const modified = Array.from(conditions);

                          modified[index].countries = data;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </div>
                  )}

                  {condition.type === 'wcCartTotal' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Display the selected block based on the total value of the current customer cart within WooCommerce. The cart value has to be within the set range for the block to be visible.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Configure Range', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextControl
                          label="Cart value is more than"
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].moreThanValue = number;

                            handleChange(modified);
                          }}
                          value={condition?.moreThanValue || false}
                          help={__('Leave blank for ANY value', 'conditional-blocks')}
                          placeholder={__('Any value', 'conditional-blocks')}
                          type="number"
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                        />
                        <TextControl
                          label="Cart value is less than"
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].lessThanValue = number;

                            handleChange(modified);
                          }}
                          value={condition?.lessThanValue || false}
                          help={__('Leave blank for ANY value', 'conditional-blocks')}
                          placeholder={__('Any value', 'conditional-blocks')}
                          type="number"
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                        />
                      </div>
                    </Fragment>
                  )}
                  {condition.type === 'wcCustomerTotalSpent' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'The selected block will be visible if the total money spent by the customer is between the set amounts.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Configure Range', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextControl
                          label="Total spend is more than"
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].moreThanValue = number;

                            handleChange(modified);
                          }}
                          value={condition?.moreThanValue || false}
                          help={__('Leave blank for ANY value', 'conditional-blocks')}
                          placeholder={__('Any value', 'conditional-blocks')}
                          type="number"
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                        />
                        <TextControl
                          label="Total spend is less than"
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].lessThanValue = number;

                            handleChange(modified);
                          }}
                          value={condition?.lessThanValue || false}
                          help={__('Leave blank for ANY value', 'conditional-blocks')}
                          placeholder={__('Any value', 'conditional-blocks')}
                          type="number"
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                        />
                      </div>
                    </Fragment>
                  )}
                  {condition.type === 'wcCartProducts' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action any of the selected products are in the customer cart.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Select Products', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <WCProductSelect
                          isMulti
                          onChange={(products) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].products = products;

                            handleChange(modified);
                          }}
                          value={condition?.products || false}
                          placeholder={__('Search products..', 'conditional-blocks')}
                        />
                      </div>

                      <RadioControl
                        label={__('Trigger', 'conditional-blocks')}
                        selected={condition?.requirement ? condition.requirement : 'any'}
                        options={[
                          { label: __('If one of the products are in the cart', 'conditional-blocks'), value: 'any' },
                          {
                            label: __('If all selected products are in  the cart', 'conditional-blocks'),
                            value: 'all',
                          },
                        ]}
                        onChange={(nextValue) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          modified[index].requirement = nextValue;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}

                  {condition.type === 'wcCartProductCategories' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action if products in the cart belong to the selected products categories.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('If Product Categories in Cart', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <WCProductCategorySelect
                          onChange={(categories) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].categories = categories;

                            handleChange(modified);
                          }}
                          isMulti
                          value={condition?.categories || false}
                          placeholder={__('Search categories..', 'conditional-blocks')}
                        />
                      </div>
                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}
                  {condition.type === 'wcCustomerRecentOrder' && (
                    <Fragment>
                      <div className="conditional-blocks-builder-modal__row">
                        {__(
                          'Trigger the Block Action if the customer has placed an WooCommerce order within the set amount of time.',
                          'conditional-blocks',
                        )}
                      </div>
                      <h4>{__('Configure', 'conditional-blocks')}</h4>
                      <div className="conditional-blocks-builder-modal__row">
                        <TextControl
                          label="Amount"
                          onChange={(number) => {
                            const modified = Array.from(conditions);
                            // Update the condition.

                            modified[index].timeAmount = number;

                            handleChange(modified);
                          }}
                          value={condition?.timeAmount || 7}
                          help={__('The amount of time to check for the recent order', 'conditional-blocks')}
                          placeholder={__('7', 'conditional-blocks')}
                          min="1"
                          type="number"
                          __nextHasNoMarginBottom={true}
                          __next40pxDefaultSize={true}
                        />
                      </div>
                      <RadioControl
                        label={__('Time format', 'conditional-blocks')}
                        selected={condition?.timeFormat ? condition.timeFormat : 'days'}
                        options={[
                          { label: __('Minutes', 'conditional-blocks'), value: 'minutes' },
                          { label: __('Hours', 'conditional-blocks'), value: 'hours' },
                          { label: __('Days', 'conditional-blocks'), value: 'days' },
                        ]}
                        onChange={(nextValue) => {
                          const modified = Array.from(conditions);
                          // Update the condition.

                          modified[index].timeFormat = nextValue;

                          handleChange(modified);
                        }}
                      />

                      <BlockAction
                        condition={condition}
                        index={index}
                        handleChange={handleChange}
                        conditions={conditions}
                      />
                    </Fragment>
                  )}

                  <ButtonWithConfirm
                    isTertiary
                    onConfirm={() => {
                      const modified = Array.from(conditions);
                      // Remove this condition.
                      modified.splice(index, 1);

                      handleChange(modified);
                    }}
                  />
                </div>
              </div>

              {shouldDisplaySeparator(index, conditions) && (
                <div className="conditional-block-separator">
                  <div className="conditional-block-separator-condition">{__('AND', 'conditional-blocks')}</div>
                </div>
              )}
            </Fragment>
          );
        })}
        <div className="conditional-blocks-builder-modal__row">
          <div className="conditional-block-new-condition">
            <Button
              isPrimary
              disabled={conditions.length && !lastCondition}
              icon="insert"
              className="conditional-blocks-builder-modal__add_condition"
              onClick={() => {
                const modified = Array.from(conditions);
                modified.push(blankCondition());
                handleChange(modified);
              }}
            >
              {conditions.length ? __('AND', 'conditional-blocks') : __('Add Condition', 'conditional-blocks')}
            </Button>
          </div>
        </div>
      </div>

      <div className="conditional-blocks-builder-modal__row" />
    </div>
  );
}

function BlockAction({ condition, conditions, index, handleChange }) {
  return (
    <Fragment>
      <h4>{__('Block Action', 'conditional-blocks')}</h4>
      <label className="conditional-blocks-builder-modal__row">
        {__('Select what happens if the condition results in true or false.', 'conditional-blocks')}
      </label>
      <Select
        classNamePrefix="conditional-blocks-select"
        className="conditional-blocks-select-container"
        isSearchable={false}
        value={
          condition?.blockAction && condition?.blockAction === 'hideBlock'
            ? { label: __('Make the block hidden, otherwise visible', 'conditional-blocks'), value: 'hideBlock' }
            : { label: __('Make the block visible, otherwise hidden', 'conditional-blocks'), value: 'showBlock' }
        } // Requires object with value & label.
        options={[
          { label: __('Make the block visible, otherwise hidden', 'conditional-blocks'), value: 'showBlock' },
          { label: __('Make the block hidden, otherwise visible', 'conditional-blocks'), value: 'hideBlock' },
        ]}
        onChange={(data) => {
          const modified = Array.from(conditions);

          modified[index].blockAction = data.value;

          handleChange(modified);
        }}
      />
    </Fragment>
  );
}
